import React, { useState, useCallback } from 'react';
import type {
  ScoreWidgetFieldFormData,
  ScoreWidgetFieldFormLabels,
  ScoreWidgetFieldFormProps,
  FieldTypeOption,
} from '../../shared/types/scoreWidgetFieldForm';

export type { ScoreWidgetFieldFormData, ScoreWidgetFieldFormLabels, ScoreWidgetFieldFormProps };

export function ScoreWidgetFieldForm({
  initialData,
  onSubmit,
  onCancel,
  onIconClick,
  errors = {},
  labels: l,
  mode,
  fieldTypeOptions,
  unitOptions,
  isSubmitting = false,
}: ScoreWidgetFieldFormProps) {
  const [formData, setFormData] = useState<ScoreWidgetFieldFormData>(initialData);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFieldChange = useCallback((field: keyof ScoreWidgetFieldFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFieldTypeSelect = useCallback((option: FieldTypeOption) => {
    setFormData(prev => ({
      ...prev,
      fieldType: option.value,
      minValue: option.minValue,
      maxValue: option.maxValue,
      stepValue: option.stepValue,
      targetValue: option.targetValue,
      units: option.units,
      icon: prev.icon === 'question-mark' || prev.icon === '' ? option.defaultIcon : prev.icon,
    }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Icon picker and Name field */}
      <div className="form-group row mb-2">
        <div className="col-12">
          <div
            className="icon-picker d-inline-block"
            onClick={onIconClick}
            style={{ cursor: 'pointer', verticalAlign: 'top' }}
          >
            <div className="icon-picker_icon-display">
              <div className={`icon-wrapper score-color--${formData.score}`}>
                <div className={`icon-flex icon-${formData.icon}`}></div>
              </div>
            </div>
          </div>
          <div className="form-floating d-inline-block" style={{ width: 'calc(100% - 80px)', verticalAlign: 'top' }}>
            <input
              type="text"
              className="form-control"
              id="fieldName"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
              autoFocus={mode === 'new'}
            />
            <label htmlFor="fieldName">{l.name}</label>
          </div>
        </div>
      </div>

      {/* Field Type Selection */}
      <div className="mb-4">
        <label className="form-label">{l.fieldType}</label>
        <div className="row g-3">
          {fieldTypeOptions.map((option) => (
            <div key={option.value} className="col-md-4">
              <div
                className={`card h-100 numeric-field-type-card ${formData.fieldType === option.value ? 'border-primary' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleFieldTypeSelect(option)}
              >
                <div className="card-body text-center">
                  <div className="mb-3">
                    <div className="icon-wrapper icon-wrapper-lg">
                      <div className={`icon-flex icon-${option.defaultIcon}`}></div>
                    </div>
                  </div>
                  <h5 className="card-title">{option.label}</h5>
                  <p className="card-text text-muted small">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Options */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-link text-decoration-none p-0"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <i className={`bi bi-chevron-${showAdvanced ? 'down' : 'right'}`}></i> {l.advancedOptions}
        </button>
        {showAdvanced && (
          <div className="card card-body mt-3">
            <div className="row">
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="minValue"
                    value={formData.minValue ?? ''}
                    onChange={(e) => handleFieldChange('minValue', e.target.value ? parseInt(e.target.value) : null)}
                    step="1"
                  />
                  <label htmlFor="minValue">{l.minValue}</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="maxValue"
                    value={formData.maxValue ?? ''}
                    onChange={(e) => handleFieldChange('maxValue', e.target.value ? parseInt(e.target.value) : null)}
                    step="1"
                  />
                  <label htmlFor="maxValue">{l.maxValue}</label>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="stepValue"
                    value={formData.stepValue ?? ''}
                    onChange={(e) => handleFieldChange('stepValue', e.target.value ? parseInt(e.target.value) : null)}
                    step="1"
                  />
                  <label htmlFor="stepValue">{l.stepValue}</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="targetValue"
                    value={formData.targetValue ?? ''}
                    onChange={(e) => handleFieldChange('targetValue', e.target.value ? parseInt(e.target.value) : null)}
                    step="1"
                  />
                  <label htmlFor="targetValue">{l.targetValue}</label>
                </div>
              </div>
            </div>
            <div className="form-floating mb-3">
              <select
                className="form-select"
                id="units"
                value={formData.units}
                onChange={(e) => handleFieldChange('units', e.target.value)}
              >
                {unitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label htmlFor="units">{l.units}</label>
            </div>
          </div>
        )}
      </div>

      {/* Form Buttons */}
      <div className="form-group d-flex justify-content-end buttons-wrapper">
        <button
          type="button"
          className="btn btn-link me-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default ScoreWidgetFieldForm;
